import React, { useState, useEffect, useCallback, useMemo } from "react";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { Icon, Form, Modal, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";

import {
  put,
  get,
  post,
  checkResponse,
  checkHttpStatus,
  checkResponseGetMany,
} from "../../../modules/httpFunctions";
import {
  getEntities,
  createQueryString,
} from "../../../modules/crudOperations";
import InputItem from "../../app/generic/inputItem";
import SelectItem from "../../app/generic/selectItem";
import { success, error } from "../../../modules/messages";
import { handleError } from "../../../modules/errorLogger";
import { loadUserRoles } from "../../../modules/staticDataLoad";
import { setPhoneNumberPattern } from "../../../modules/consignmentFunctions";
import { isSuperAdmin, isSuperUser, roleNames } from "../../../modules/session";

import "./index.css";

const defaultIcon = <span />;

const checkUserNameDebounced = AwesomeDebouncePromise(
  (username, setUserNameIcon, setCannotSave, intl) => {
    if (username) {
      setUserNameIcon(<Icon type="loading" />);
      get(`user${createQueryString({ username })}`)
        .then((r) => {
          const used = checkHttpStatus(r) && checkResponseGetMany(r);
          setCannotSave(used);
          setUserNameIcon(<AvailabilityIcon intl={intl} used={used} />);
        })
        .catch((err) => {
          setUserNameIcon(defaultIcon);
          handleError(err);
        });
    } else {
      setUserNameIcon(defaultIcon);
    }
  },
  1000
);

const AvailabilityIcon = ({ used, intl }) => (
  <Tooltip
    title={
      used
        ? intl.formatMessage({
            id: "user.usedUsername",
            defaultMessage:
              "Uživatelské jméno je již použité, zvolte prosím jiné.",
          })
        : intl.formatMessage({
            id: "user.freeUsername",
            defaultMessage: "Uživatelské jméno je volné pro použití.",
          })
    }
  >
    <Icon
      type={used ? "close-circle" : "check-circle"}
      className={used ? "user-form-red" : "user-form-green"}
    />
  </Tooltip>
);

function UserForm({
  user,
  form,
  visible,
  loadUsers,
  onSetUser,
  setVisible,
  oneTimeLogin,
}) {
  const intl = useIntl();
  const { getFieldDecorator } = form;
  const formItemLayout = { colon: false };
  const { data } = useSelector((state) => state.language);
  const currentUser = useSelector((state) => state.session.user);
  const phonePattern = setPhoneNumberPattern("CZ");

  const superAdmin = useMemo(
    () => isSuperAdmin(currentUser && currentUser.role),
    [currentUser]
  );
  const superUser = useMemo(
    () => isSuperUser(currentUser && currentUser.role),
    [currentUser]
  );

  const [languages, setLanguages] = useState();
  useEffect(() => {
    getEntities("language", setLanguages);
  }, []);

  const [userRoles, setUserRoles] = useState();
  useEffect(() => {
    superAdmin && loadUserRoles(setUserRoles);
  }, [superAdmin]);

  const [loading, setLoading] = useState();
  const handleSubmit = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {
        setLoading(true);
        (user
          ? put(`user/${user.id}`, values)
          : post(`user${oneTimeLogin ? "/one-time-login" : ""}`, values)
        )
          .then((r) => {
            setLoading(false);
            if (checkHttpStatus(r) && checkResponse(r)) {
              success(
                r.data.message ||
                  intl.formatMessage({
                    id: "user.saved",
                    defaultMessage: "Uživatel byl úspěšně uložen.",
                  })
              );
              setVisible(false);
              loadUsers();
            } else {
              error(
                (r.data && r.data.message) ||
                  intl.formatMessage({
                    id: "user.saveError",
                    defaultMessage: "Při ukládání uživatele nastala chyba!",
                  }),
                10
              );
            }
          })
          .catch((err) => {
            setLoading(false);
            error(
              intl.formatMessage({
                id: "user.saveError",
                defaultMessage: "Při ukládání uživatele nastala chyba!",
              }),
              10
            );
            handleError(err);
          });
      }
    });
  }, [intl, form, user, loadUsers, setVisible, oneTimeLogin]);

  const handleCancel = useCallback(() => {
    setVisible(false);
    onSetUser && onSetUser();
  }, [setVisible, onSetUser]);

  const [cannotSave, setCannotSave] = useState(false);
  const checkUserName = useCallback(
    (username) =>
      checkUserNameDebounced(username, setUserNameIcon, setCannotSave, intl),
    [intl]
  );

  const onEmailChange = useCallback(
    (e) => {
      checkUserName(e.target.value);
      form.setFieldsValue({ username: e.target.value });
      return e.target.value;
    },
    [form, checkUserName]
  );

  const [userNameIcon, setUserNameIcon] = useState(defaultIcon);
  const onUserNameChange = useCallback(
    (e) => {
      e.target.value && checkUserName(e.target.value);
      return e.target.value;
    },
    [checkUserName]
  );

  return (
    <Modal
      width={500}
      visible={visible}
      onOk={handleSubmit}
      cancelText={intl.formatMessage({
        id: "messages.close",
        defaultMessage: "Zavřít",
      })}
      okText={
        oneTimeLogin
          ? intl.formatMessage({
              id: "oneTimeLogin.save",
              defaultMessage: "Vytvořit jednorázové přihlášení",
            })
          : intl.formatMessage({
              id: "user.save",
              defaultMessage: "Uložit uživatele",
            })
      }
      onCancel={handleCancel}
      confirmLoading={loading}
      okButtonProps={{ disabled: cannotSave }}
      title={
        oneTimeLogin
          ? intl.formatMessage({
              id: "oneTimeLogin.new.long",
              defaultMessage: "Nové jednorázové přihlášení",
            })
          : user
          ? intl.formatMessage({
              id: "user.edit",
              defaultMessage: "Editace uživatele",
            })
          : intl.formatMessage({
              id: "user.new",
              defaultMessage: "Nový uživatel",
            })
      }
    >
      <Form {...formItemLayout}>
        <InputItem
          label={intl.formatMessage({
            id: "user.firstname",
            defaultMessage: "Jméno",
          })}
          fieldName="firstname"
          message={intl.formatMessage({
            id: "user.emptyFirstName",
            defaultMessage: "Zadejte jméno!",
          })}
          formItemLayout={formItemLayout}
          getFieldDecorator={getFieldDecorator}
          initialValue={user && user.firstname}
        />
        <InputItem
          label={intl.formatMessage({
            id: "user.lastname",
            defaultMessage: "Příjmení",
          })}
          fieldName="lastname"
          message={intl.formatMessage({
            id: "user.emptyLastName",
            defaultMessage: "Zadejte příjmení!",
          })}
          formItemLayout={formItemLayout}
          getFieldDecorator={getFieldDecorator}
          initialValue={user && user.lastname}
        />
        {languages && (
          <SelectItem
            options={languages}
            fieldName="languageId"
            label={intl.formatMessage({
              id: "language",
              defaultMessage: "Jazyk",
            })}
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            initialValue={(user && user.languageId) || (data && data.id) || 1}
          />
        )}
        <InputItem
          email
          label={intl.formatMessage({
            id: "user.email",
            defaultMessage: "E-mail",
          })}
          fieldName="email"
          message={
            oneTimeLogin
              ? undefined
              : intl.formatMessage({
                  id: "user.emptyEmail",
                  defaultMessage: "Zadejte email uživatele!",
                })
          }
          formItemLayout={formItemLayout}
          getFieldDecorator={getFieldDecorator}
          initialValue={user && user.email}
          onValueChange={!user && !oneTimeLogin && onEmailChange}
        />
        <InputItem
          allowClear={false}
          fieldName="username"
          suffix={userNameIcon}
          label={
            oneTimeLogin
              ? intl.formatMessage({
                  id: "oneTimeLogin.phone",
                  defaultMessage: "Telefonní číslo",
                })
              : intl.formatMessage({
                  id: "user.username",
                  defaultMessage: "Uživatelské jméno",
                })
          }
          message={
            oneTimeLogin
              ? intl.formatMessage({
                  id: "oneTimeLogin.phone.empty",
                  defaultMessage: "Zadejte telefonní číslo",
                })
              : intl.formatMessage({
                  id: "user.emptyUserName",
                  defaultMessage: "Zadejte uživatelské jméno!",
                })
          }
          disabled={user ? true : false}
          formItemLayout={formItemLayout}
          getFieldDecorator={getFieldDecorator}
          initialValue={user && user.username}
          pattern={oneTimeLogin && phonePattern}
          onValueChange={!oneTimeLogin && onUserNameChange}
        />
        {superAdmin && userRoles && !oneTimeLogin && (
          <SelectItem
            allowClear
            options={userRoles}
            fieldName="userRoleId"
            label={intl.formatMessage({
              id: "user.userrole",
              defaultMessage: "Uživatelská role",
            })}
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            initialValue={
              (user && user.userRoleId) ||
              (userRoles !== "e" &&
                userRoles.find((u) => u.name === roleNames.user).id) ||
              ""
            }
          />
        )}
        {superUser && (
          <InputItem
            fieldName="note"
            formItemLayout={formItemLayout}
            initialValue={user && user.note}
            label={intl.formatMessage({
              id: "user.note",
              defaultMessage: "Interní poznámka",
            })}
            getFieldDecorator={getFieldDecorator}
          />
        )}
      </Form>
    </Modal>
  );
}

export default Form.create()(UserForm);
